import { z } from "zod";

export const contractsSummarySchema = z.object({
  total_amount: z.number().or(z.string().transform(Number)),
  total_spend_till_now: z.number().or(z.string().transform(Number)),
  estimated_total_spend: z.number().or(z.string().transform(Number)),
  average_daily_spend: z.number().or(z.string().transform(Number)),
});

export type ContractsSummary = z.infer<typeof contractsSummarySchema>;

export const ContractTableSchema = z.object({
  resource_id: z.string(),
  tablename: z.string(),
  database: z.string(),
  schema: z.string(),
  path: z.string(),
});

export const contractItemSchema = z
  .object({
    rk: z.string(),
    contract_number: z.number().nullable().optional(),
    start_date: z.string(),
    end_date: z.string(),
    expiration_date: z.string().nullable().optional(),
    contract_item: z.string(),
    currency: z.string(),
    amount: z.number().or(z.string().transform(Number)),
    contract_modified_date: z.string(),
    published_tag: z.string().optional().nullable(),
    publisher_last_updated_epoch_ms: z
      .number()
      .or(z.string().transform(Number))
      .optional()
      .nullable(),
  })
  .passthrough();

export type ContractItem = z.infer<typeof contractItemSchema>;

// Contract Detail Response Schema
export const ContractDetailResponseSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  filter: z.string().optional(),
  level: z.string(),
  tables: z.array(ContractTableSchema),
  status: z.string(),
  last_violation_timestamp: z.string().optional(),
  last_updated: z.string(),
  version: z.string(),
  created_at: z.string(),
  slack_channel: z.string().optional(),
  schedule: z.string(),
  registry_id: z.number(),
  registry_name: z.string(),
  owners: z.array(z.string()).optional(),
  alias: z.array(z.string()).optional(),
  no_of_violations: z.number(),
  week_data: z.object({
    error: z.number(),
    warning: z.number(),
    policies: z.number(),
    validations: z.number(),
  }),
  code: z.string(),
  can_validate: z.boolean(),
  is_scheduled: z.boolean(),
  policies: z.array(
    z.object({
      policy_id: z.number(),
      name: z.string(),
      description: z.string().optional(),
      last_violation_timestamp: z.string().optional(),
      level: z.string(),
      tables: z.array(ContractTableSchema),
      filter: z.string().optional(),
    })
  ),
});

export type ContractDetailResponse = z.infer<typeof ContractDetailResponseSchema>;

// Validate Contract Request/Response
export interface ValidateContractRequest {
  registryId: string;
  contractId: string;
}

export const ValidateContractResponseSchema = z.object({
  contract_name: z.string(),
  is_valid: z.boolean(),
  ok: z.boolean().optional(),
  event_id: z.number(),
  num_violations: z.number(),
  validation_exceptions: z
    .array(
      z.object({
        policy_name: z.string(),
        term_violation_id: z.string(),
        level: z.string(),
        metric: z.string(),
        condition: z.string().optional(),
        actual: z.string().optional(),
        message: z.string(),
        status: z.string(),
      })
    )
    .optional(),
  event_url: z.string(),
});

export type ValidateContractResponse = z.infer<typeof ValidateContractResponseSchema>;
